/* =====================================================================
   Rumble & Rest — theme script

   Not in this file, deliberately: any hard-coded review, rating or review
   count. Reviews come from real order-backed data or the section does not
   render. See the note in the guarantee block.
   ===================================================================== */

const $  = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
/* The store's currency, never a hard-coded one: Shopify exposes the active
   currency (which follows the market the visitor is in), and the drawer carries
   the cart's own code as a fallback for when that global is missing. Hard-coding
   it is how the drawer ended up printing GBP after the store moved to USD. */
const shopCurrency = () =>
  window.Shopify?.currency?.active
  || document.getElementById('cart-drawer')?.dataset.currency
  || 'USD';
const money = c => new Intl.NumberFormat(document.documentElement.lang || 'en-US',
  { style: 'currency', currency: shopCurrency() }).format(c / 100);
const esc = s => String(s ?? '').replace(/[&<>"']/g, c =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* ---------------------------------------------------------------
   Accordions — one initialiser for every disclosure on the page
   --------------------------------------------------------------- */
function initAccordions(root = document) {
  $$('.faq__b', root).forEach(btn => {
    if (btn.dataset.bound) return;
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;
    btn.dataset.bound = '1';
    panel.style.height = '0px';
    const set = open => { panel.style.height = open ? panel.scrollHeight + 'px' : '0px'; };
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      set(!open);
    });
    addEventListener('resize', () => {
      if (btn.getAttribute('aria-expanded') === 'true') set(true);
    });
  });
}

/* ---------------------------------------------------------------
   Generic slide-over: mobile menu and cart drawer share the behaviour
   --------------------------------------------------------------- */
function slideOver(panel, openers, { onOpen } = {}) {
  if (!panel) return null;
  let lastFocus = null;
  const focusables = () =>
    $$('a[href], button:not([disabled]), input, select, textarea', panel).filter(el => el.offsetParent !== null);

  function onKey(e) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    const f = focusables(); if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  function open() {
    lastFocus = document.activeElement;
    panel.dataset.open = 'true';
    document.body.classList.add('is-locked');
    openers.forEach(o => o.setAttribute?.('aria-expanded', 'true'));
    onOpen?.();
    focusables()[0]?.focus();
    document.addEventListener('keydown', onKey);
  }
  function close() {
    if (panel.dataset.open !== 'true') return;
    panel.dataset.open = 'false';
    document.body.classList.remove('is-locked');
    openers.forEach(o => o.setAttribute?.('aria-expanded', 'false'));
    document.removeEventListener('keydown', onKey);
    (lastFocus || openers[0])?.focus?.();
  }
  panel.addEventListener('click', e => {
    if (e.target.closest('[data-close], [data-cart-close], .overlay__veil, .drawer__veil')) close();
    else if (panel.classList.contains('overlay') && e.target.closest('a[href]')) close();
  });
  return { open, close };
}

const menu = slideOver($('#menu'), [$('#burger')].filter(Boolean));
$('#burger')?.addEventListener('click', () => menu?.open());

/* ---------------------------------------------------------------
   Cart drawer + AJAX add to cart
   --------------------------------------------------------------- */
const Cart = (() => {
  const panel = $('#cart-drawer');
  const body = $('#drawer-body');
  const total = $('#drawer-total');
  const bundleSlot = $('#drawer-bundle');
  const openers = $$('[data-cart-open]');
  const drawer = slideOver(panel, openers);
  let bundleData = null;
  try { bundleData = JSON.parse($('#bundle-data')?.textContent || 'null'); } catch { /* no bundle configured */ }

  function countBadge(n) {
    const el = $('#cart-count');
    if (el) el.textContent = n;
    openers.forEach(o => o.setAttribute('aria-label', `Cart, ${n} item${n === 1 ? '' : 's'}`));
  }

  function render(cart) {
    if (!body) return;
    countBadge(cart.item_count);
    if (total) total.textContent = money(cart.total_price);
    /* an empty cart hides the footer: a live Checkout button on nothing just
       lands the shopper on an error page */
    if (panel) panel.dataset.empty = cart.items.length ? 'false' : 'true';

    body.innerHTML = cart.items.length
      ? cart.items.map(i => `
        <div class="dline" data-key="${esc(i.key)}">
          <img src="${esc(i.image ? i.image.replace(/(\.[a-z]+)(\?|$)/i, '_144x$1$2') : '')}"
               alt="${esc(i.product_title)}" width="72" height="72" loading="lazy">
          <div class="dline__i">
            <span class="dline__n">${esc(i.product_title)}</span>
            ${/default/i.test(i.variant_title || '') ? '' : `<span class="dline__v">${esc(i.variant_title)}</span>`}
            <div class="dline__ctl">
              <div class="qty" role="group" aria-label="Quantity for ${esc(i.product_title)}">
                <button class="qty__b" type="button" data-qty="${i.quantity - 1}" data-key="${esc(i.key)}" aria-label="Decrease quantity">&minus;</button>
                <span class="qty__n" aria-live="polite">${i.quantity}</span>
                <button class="qty__b" type="button" data-qty="${i.quantity + 1}" data-key="${esc(i.key)}" aria-label="Increase quantity">+</button>
              </div>
              <button class="dline__rm" type="button" data-qty="0" data-key="${esc(i.key)}">Remove</button>
            </div>
          </div>
          <span class="dline__p">${money(i.final_line_price)}</span>
        </div>`).join('')
      : '<p class="drawer__empty">Your cart is empty.</p>';

    renderBundle(cart);
  }

  /* The same one-tap pair as the PDP, offered when the cart holds a single
     line and the partner product is not already in it. */
  function renderBundle(cart) {
    if (!bundleSlot) return;
    bundleSlot.innerHTML = '';
    if (!bundleData || cart.items.length !== 1) return;
    /* Offer whichever half of the pair is not in the cart. */
    const inCart = cart.items[0].variant_id;
    let offer = null;
    if (inCart === bundleData.a.id) offer = bundleData.b;
    else if (inCart === bundleData.b.id) offer = bundleData.a;
    if (!offer || !offer.img) return;
    const saving = (bundleData.discount > 0 && offer.wasText)
      ? `<span class="bundle__was">${esc(offer.wasText)}</span><span class="bundle__save">save ${esc(offer.saveText)}</span>` : '';
    bundleSlot.innerHTML = `
      <div class="bundle" style="margin:0 var(--s3) var(--s3)">
        <p class="bundle__h">Add to your order</p>
        <div class="bundle__r">
          <img class="bundle__img" src="${esc(offer.img)}" alt="${esc(offer.title)}" width="64" height="64" loading="lazy">
          <div>
            <span class="bundle__n">${esc(offer.title)}</span>
            <span class="bundle__p"><span class="bundle__now">${esc(offer.priceText)}</span>${saving}</span>
          </div>
          <button class="btn btn--primary" type="button" data-bundle-add data-ids="${offer.id}">Add</button>
        </div>
      </div>`;
  }

  async function refresh() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
      render(cart);
      return cart;
    } catch { return null; }
  }

  /* ids: array of variant ids. Adds them in one request. */
  async function add(ids) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: ids.map(id => ({ id: Number(id), quantity: 1 })) })
    });
    if (!res.ok) throw new Error(await res.text());
    await refresh();
    drawer?.open();
  }

  /* Quantity stepper and Remove. key is the cart line key, quantity 0 removes.
     The panel is locked while the request is in flight so a double tap cannot
     queue two changes against a line that no longer exists. */
  async function change(key, quantity) {
    if (panel) panel.dataset.busy = 'true';
    try {
      const cart = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) })
      }).then(r => r.json());
      render(cart);
      return cart;
    } catch {
      return refresh();
    } finally {
      if (panel) panel.dataset.busy = 'false';
    }
  }

  panel?.addEventListener('click', e => {
    const btn = e.target.closest('[data-qty][data-key]');
    if (!btn || panel.dataset.busy === 'true') return;
    e.preventDefault();
    change(btn.dataset.key, btn.dataset.qty);
  });

  openers.forEach(o => o.addEventListener('click', e => {
    /* keep the plain link working if the drawer never initialised */
    if (!panel) return;
    e.preventDefault();
    refresh().then(() => drawer?.open());
  }));

  return { add, change, refresh, open: () => drawer?.open(), hasBundle: () => !!bundleData };
})();

/* product form -> AJAX, so adding never costs a page load */
$('#buy')?.addEventListener('submit', async e => {
  const form = e.target;
  const id = form.querySelector('[name="id"]')?.value;
  if (!id) return;
  e.preventDefault();
  const btn = form.querySelector('.btn--buy');
  const label = btn?.textContent;
  if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
  try { await Cart.add([id]); }
  catch { form.submit(); return; }
  finally { if (btn) { btn.disabled = false; btn.textContent = label; } }
});

/* bundle buttons, on the PDP and inside the drawer */
document.addEventListener('click', async e => {
  const btn = e.target.closest('[data-bundle-add]');
  if (!btn) return;
  e.preventDefault();
  const ids = btn.dataset.ids.split(',').map(s => s.trim()).filter(Boolean);
  const label = btn.textContent;
  btn.disabled = true; btn.textContent = 'Adding…';
  try { await Cart.add(ids); } catch { location.href = '/cart'; }
  finally { btn.disabled = false; btn.textContent = label; }
});

/* ---------------------------------------------------------------
   Product gallery — swipe, dots, thumbnails, zoom
   --------------------------------------------------------------- */
(function () {
  const track = $('#gal-track');
  const vp = $('#gal-vp');
  if (!track || !vp) return;
  const slides = $$('.gal__s', track);
  if (!slides.length) return;
  const dots = $$('.gal__dot', $('#gal-dots'));
  const thumbs = $$('.gal__t', $('#gal-thumbs'));
  let i = 0;

  const apply = () => { track.style.transform = `translate3d(${-i * 100}%,0,0)`; };
  function go(n) {
    i = Math.max(0, Math.min(slides.length - 1, n));
    apply();
    dots.forEach((d, k) => { d.setAttribute('aria-selected', String(k === i)); d.tabIndex = k === i ? 0 : -1; });
    thumbs.forEach((t, k) => { t.setAttribute('aria-selected', String(k === i)); t.tabIndex = k === i ? 0 : -1; });
  }
  dots.forEach(d => d.addEventListener('click', () => go(+d.dataset.i)));
  thumbs.forEach(t => t.addEventListener('click', () => go(+t.dataset.i)));

  [$('#gal-dots'), $('#gal-thumbs')].forEach(rail => rail?.addEventListener('keydown', e => {
    if (!['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
    e.preventDefault();
    const next = e.key === 'ArrowRight' ? i + 1 : i - 1;
    go((next + slides.length) % slides.length);
    (rail.children[i])?.focus();
  }));

  /* swipe */
  let x0 = null, dx = 0, dragging = false;
  track.addEventListener('pointerdown', e => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragging = true; x0 = e.clientX; dx = 0;
    track.classList.add('is-drag');
  });
  track.addEventListener('pointermove', e => {
    if (!dragging) return;
    dx = e.clientX - x0;
    track.style.transform = `translate3d(calc(${-i * 100}% + ${dx}px),0,0)`;
  });
  const end = () => {
    if (!dragging) return;
    dragging = false;
    track.classList.remove('is-drag');
    const threshold = Math.min(80, vp.clientWidth * 0.18);
    if (dx <= -threshold) go(i + 1);
    else if (dx >= threshold) go(i - 1);
    else apply();
    dx = 0;
  };
  track.addEventListener('pointerup', end);
  track.addEventListener('pointercancel', end);
  track.addEventListener('pointerleave', end);

  /* zoom into a lightbox; pinch-zoom is native inside it */
  const lbx = $('#lbx'), lbxImg = $('#lbx-img');
  function openZoom() {
    const s = slides[i];
    if (!lbx || !lbxImg || !s || s.dataset.kind !== 'image') return;
    lbxImg.src = s.dataset.full;
    lbxImg.alt = s.dataset.alt || '';
    lbx.dataset.open = 'true';
    document.body.classList.add('is-locked');
    $('[data-lbx-close]', lbx)?.focus();
  }
  function closeZoom() {
    if (!lbx) return;
    lbx.dataset.open = 'false';
    document.body.classList.remove('is-locked');
  }
  $('#gal-zoom')?.addEventListener('click', openZoom);
  track.addEventListener('click', e => {
    if (dx !== 0) return;                       /* a drag, not a tap */
    if (e.target.closest('.gal__s--zoomable') && matchMedia('(min-width:900px)').matches) openZoom();
  });
  lbx?.addEventListener('click', e => { if (!e.target.closest('img') || e.target.closest('[data-lbx-close]')) closeZoom(); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && lbx?.dataset.open === 'true') closeZoom(); });

  go(0);
})();

/* ---------------------------------------------------------------
   Variant selection
   --------------------------------------------------------------- */
(function () {
  const sel = $('#variant-select');
  if (!sel) return;
  const chosen = [];
  $$('.opt').forEach(b => {
    if (b.getAttribute('aria-pressed') === 'true') chosen[+b.dataset.option - 1] = b.dataset.value;
  });
  const sync = () => {
    const want = chosen.filter(Boolean).join(' / ');
    const opt = [...sel.options].find(o => (o.dataset.title || '').trim() === want);
    if (!opt) return;
    sel.value = opt.value;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    ['#price-now', '#add-price', '#sticky-price'].forEach(s => {
      const el = $(s); if (el && opt.dataset.price) el.textContent = opt.dataset.price;
    });
    const bundleBtn = $('[data-bundle]')?.querySelector('[data-bundle-add]');
    if (bundleBtn) {
      const ids = bundleBtn.dataset.ids.split(',');
      ids[0] = opt.value;
      bundleBtn.dataset.ids = ids.join(',');
    }
  };
  $$('.opt').forEach(btn => btn.addEventListener('click', () => {
    chosen[+btn.dataset.option - 1] = btn.dataset.value;
    $$(`.opt[data-option="${btn.dataset.option}"]`).forEach(x =>
      x.setAttribute('aria-pressed', String(x === btn)));
    sync();
  }));
})();

/* ---------------------------------------------------------------
   Computed delivery date

   A real cutoff, read in the warehouse timezone, aware of weekends and public
   holidays. Deliberately NOT a live countdown: on a store with no reviews a
   ticking timer reads as a dropshipping tell, and a seconds display invites
   the reader to watch it rather than buy. The useful half — when it ships and
   when it lands — is stated once, in body colour, and recomputed only on load.
   --------------------------------------------------------------- */
(function () {
  const box = $('.deliv');
  const out = $('#deliv-msg');
  if (!box || !out) return;

  const CUTOFF = parseInt(box.dataset.cutoff ?? '14', 10);
  const MIN = parseInt(box.dataset.minDays ?? '2', 10);
  const MAX = parseInt(box.dataset.maxDays ?? '4', 10);
  const HOLIDAYS = new Set((box.dataset.holidays || '').split(',').map(s => s.trim()).filter(Boolean));
  const TZ = box.dataset.tz || 'America/New_York';
  const TZLABEL = box.dataset.tzlabel || 'ET';
  /* 14 -> "2pm". The site says "2pm ET" everywhere; a 24h clock here would be a
     third format for the same fact. */
  const clockLabel = h => `${((h + 11) % 12) + 1}${h < 12 ? 'am' : 'pm'} ${TZLABEL}`;
  if (Number.isNaN(CUTOFF)) return;

  const iso = d => d.toISOString().slice(0, 10);
  const isWorkday = d => d.getUTCDay() !== 0 && d.getUTCDay() !== 6 && !HOLIDAYS.has(iso(d));
  const addWorkdays = (d, n) => {
    const out = new Date(d);
    while (n > 0) { out.setUTCDate(out.getUTCDate() + 1); if (isWorkday(out)) n--; }
    return out;
  };
  const fmt = d => new Intl.DateTimeFormat('en-GB',
    { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(d);
  const fmtShort = d => new Intl.DateTimeFormat('en-GB',
    { weekday: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);

  function storeNow() {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }).formatToParts(new Date()).map(x => [x.type, x.value]));
    const h = p.hour === '24' ? 0 : +p.hour;
    return { date: new Date(Date.UTC(+p.year, +p.month - 1, +p.day)), h, m: +p.minute, s: +p.second };
  }

  function tick() {
    let now;
    try { now = storeNow(); } catch { return; }        /* no tz data: keep the server sentence */

    const todayWorkday = isWorkday(now.date);
    const beforeCutoff = now.h < CUTOFF;
    const shipsToday = todayWorkday && beforeCutoff;

    const ship = shipsToday ? now.date : addWorkdays(now.date, 1);
    const early = addWorkdays(ship, MIN);
    const late = addWorkdays(ship, MAX);
    const arrival = MIN === MAX ? fmt(early) : `${fmtShort(early)} – ${fmt(late)}`;

    if (shipsToday) {
      const mins = (CUTOFF * 60) - (now.h * 60 + now.m);
      const hh = Math.floor(mins / 60), mm = mins % 60;
      const left = hh > 0 ? `${hh} hr ${mm} min` : `${mm} min`;
      out.innerHTML =
        `Ships today if you order in the next <b>${left}</b> &rarr; arrives <span class="deliv__date">${arrival}</span>` +
        `<span class="deliv__note">Free tracked shipping</span>`;
    } else {
      const when = shipsToday ? 'today' : (isWorkday(now.date) ? 'tomorrow' : fmtShort(ship));
      out.innerHTML =
        `Ships ${when}, arrives <span class="deliv__date">${arrival}</span>` +
        `<span class="deliv__note">Today's carrier pickup has gone &mdash; order by ${clockLabel(CUTOFF)} to catch the next one</span>`;
    }
  }

  tick();   /* once on load — no interval, nothing ticks */
})();

/* ---------------------------------------------------------------
   Fit checker — make, model, year, verdict
   --------------------------------------------------------------- */
(function () {
  const root = $('#fitck');
  const raw = $('#fitck-data');
  if (!root || !raw) return;

  let data;
  try { data = JSON.parse(raw.textContent); } catch { return; }
  if (!Array.isArray(data) || !data.length) return;

  const makeSel = $('#fitck-make'), modelSel = $('#fitck-model'), yearSel = $('#fitck-year');
  const out = $('#fitck-out'), title = $('#fitck-title'), bodyEl = $('#fitck-body');
  const buy = $('#fitck-buy'), cap = $('#fitck-cap'), bodyField = $('#fitck-body-field');

  const opt = (v, t) => { const o = document.createElement('option'); o.value = v; o.textContent = t; return o; };
  const reset = (sel, ph) => { sel.innerHTML = ''; sel.append(opt('', ph)); sel.disabled = true; };

  /* "2013-2026" | "2019" | undefined (= any year) */
  function yearsOf(entry) {
    if (!entry.years) return null;
    const m = String(entry.years).match(/^(\d{4})\s*[-–]\s*(\d{4})$/);
    if (m) { const a = +m[1], b = +m[2], list = []; for (let y = b; y >= a; y--) list.push(y); return list; }
    const one = String(entry.years).match(/^(\d{4})$/);
    return one ? [+one[1]] : null;
  }

  data.slice().sort((a, b) => a.make.localeCompare(b.make))
    .forEach(m => makeSel.append(opt(m.make, m.make)));

  makeSel.addEventListener('change', () => {
    reset(modelSel, 'Choose a model'); reset(yearSel, 'Choose a year'); hide();
    const make = data.find(m => m.make === makeSel.value);
    if (!make) return;
    (make.models || []).slice().sort((a, b) => a.model.localeCompare(b.model))
      .forEach(m => modelSel.append(opt(m.model, m.model)));
    modelSel.disabled = false;
  });

  modelSel.addEventListener('change', () => {
    reset(yearSel, 'Choose a year'); hide();
    const make = data.find(m => m.make === makeSel.value);
    const model = make?.models?.find(m => m.model === modelSel.value);
    if (!model) return;
    const years = yearsOf(model);
    if (years) years.forEach(y => yearSel.append(opt(String(y), String(y))));
    else {
      const now = new Date().getUTCFullYear();
      for (let y = now + 1; y >= now - 20; y--) yearSel.append(opt(String(y), String(y)));
    }
    yearSel.disabled = false;
  });

  yearSel.addEventListener('change', () => { if (yearSel.value) verdict(); });

  function hide() { out.dataset.show = 'false'; }

  function verdict() {
    const make = data.find(m => m.make === makeSel.value);
    const model = make?.models?.find(m => m.model === modelSel.value);
    const year = +yearSel.value;

    /* Anything we do not hold data for is "check", never a guess. */
    let v = 'check';
    if (model) {
      const years = yearsOf(model);
      const inRange = !years || years.includes(year);
      v = inRange ? (model.verdict || 'check') : 'check';
    }
    const car = `${makeSel.value} ${modelSel.value}`.trim();
    const note = model?.note;

    out.dataset.verdict = v;
    out.dataset.show = 'true';
    buy.hidden = v !== 'fits';
    cap.hidden = v === 'fits';

    if (v === 'fits') {
      title.textContent = `Yes, this fits your ${year} ${car}`;
      bodyEl.textContent = note || 'Straight fit, no adapters and nothing extra to buy.';
    } else if (v === 'no') {
      title.textContent = `Not a match for a ${year} ${car}`;
      bodyEl.textContent = note || 'We would rather tell you now than take the order. Leave your email and we will let you know if that changes.';
    } else {
      title.textContent = `We are not certain about a ${year} ${car}`;
      bodyEl.textContent = note || 'We have not measured this one ourselves and we are not going to guess. Leave your email and we will confirm within a business day.';
    }
    if (bodyField) bodyField.value = `Fit check for ${year} ${car} — verdict shown: ${v}`;
  }
})();

/* ---------------------------------------------------------------
   Sticky buy bar — appears once the main CTA scrolls away, hides again
   over the footer CTA so it never covers it
   --------------------------------------------------------------- */
(function () {
  const bar = $('#sticky');
  const cta = $('.btn--buy');
  if (!bar || !cta) return;
  const footCta = $('.finale__in .btn--primary');

  let queued = false;
  const update = () => {
    queued = false;
    const past = cta.getBoundingClientRect().bottom < 0;
    const footVisible = footCta
      ? footCta.getBoundingClientRect().top < innerHeight - 40
      : false;
    bar.dataset.show = String(past && !footVisible);
  };
  const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll);
  update();

  $('#sticky-add')?.addEventListener('click', () => cta.click());
})();

/* ---------------------------------------------------------------
   Review carousel — real, order-backed reviews only
   --------------------------------------------------------------- */
(function () {
  const track = $('#rev-track');
  const root = $('#rev-carousel');
  const REVIEWS = Array.isArray(window.__productReviews) ? window.__productReviews : [];
  if (!track || !root || !REVIEWS.length) return;

  const stars = n => '★★★★★'.slice(0, n) + '☆☆☆☆☆'.slice(0, 5 - n);
  const N = REVIEWS.length;
  $('#rev-total').textContent = String(N).padStart(2, '0');

  track.innerHTML = REVIEWS.map(r => `
    <li class="carousel__slide">
      <article class="rcard">
        ${r.img ? `<img class="rcard__img" src="${esc(r.img)}" alt="" width="600" height="375" loading="lazy">`
                : `<div class="rcard__ph" aria-hidden="true">${esc((r.name || '?').trim()[0])}</div>`}
        <div class="rcard__s" aria-hidden="true">${stars(r.stars)}</div>
        <p class="sr-only">Rated ${esc(r.stars)} out of 5</p>
        <p class="rcard__t">${esc(r.text)}</p>
        <footer class="rcard__f">
          <b>${esc(r.name)}</b>
          <span class="rcard__v"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 13 4.5 4.5L19 7"/></svg>Verified purchase</span>
          <span>${esc([r.location, r.pet].filter(Boolean).join(' · '))}</span>
        </footer>
      </article>
    </li>`).join('');

  let i = 0;
  const slide = () => track.querySelector('.carousel__slide');
  const step = () => {
    const s = slide(); if (!s) return 0;
    return s.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).gap) || 0);
  };
  function go(n) {
    i = (n + N) % N;
    track.style.transform = `translate3d(${-i * step()}px,0,0)`;
    $('#rev-pos').textContent = String(i + 1).padStart(2, '0');
    $('#rev-live').textContent = `Review ${i + 1} of ${N}`;
  }
  $('#rev-prev').addEventListener('click', () => go(i - 1));
  $('#rev-next').addEventListener('click', () => go(i + 1));
  root.addEventListener('keydown', e => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(i + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(i - 1); }
  });
  addEventListener('resize', () => go(i));
  go(0);
})();

initAccordions();

/* ---------------------------------------------------------------
   TikTok embeds — load the script only where one exists, and only
   when the section is near the viewport. Nothing above the fold
   should wait on a third-party origin.
   --------------------------------------------------------------- */
(function () {
  const blocks = $$('blockquote.tiktok-embed');
  if (!blocks.length) return;
  let loaded = false;
  const load = () => {
    if (loaded) return;
    loaded = true;
    const sc = document.createElement('script');
    sc.async = true;
    sc.src = 'https://www.tiktok.com/embed.js';
    document.body.appendChild(sc);
  };
  if (!('IntersectionObserver' in window)) { load(); return; }
  const io = new IntersectionObserver(entries => {
    if (entries.some(e => e.isIntersecting)) { load(); io.disconnect(); }
  }, { rootMargin: '400px' });
  blocks.forEach(b => io.observe(b));
})();
